import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App.tsx'
import './index.css'
import { applyBrand, getBrand } from './lib/brand'
import { apiBase } from './lib/apiBase'
import { initSentry, setSentryTenant } from './lib/sentry'
import { initAnalytics, setAnalyticsTenant } from './lib/analytics'
import { configureLocale, setApiBaseUrl } from '@besiktning/ui'

// Start error/performance monitoring first so failures during startup (brand
// load, auth) are captured. No-op when VITE_SENTRY_DSN is unset.
initSentry()
// Product analytics (pageviews + staff events). No-op when VITE_POSTHOG_KEY unset.
initAnalytics()

// Point the shared axios client at the tenant's `api.` subdomain (see lib/apiBase).
// Must run before any request — brand load, auth, dashboard — fires.
setApiBaseUrl(apiBase())

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

function render() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

// Load white-label branding (title, theme, logo) BEFORE first paint so the logo
// and colors are correct on the very first render. applyBrand never rejects
// (it falls back to defaults), so render always runs. Also push the brand's
// locale/currency into @besiktning/ui so its shared components + formatters match.
applyBrand()
  .then(() => {
    const b = getBrand()
    configureLocale({ locale: b.locale, currency: b.currency, timeZone: b.time_zone })
    setSentryTenant(b.app_name)
    setAnalyticsTenant(b.app_name)
  })
  .finally(render)
